import React, { useState } from 'react';
import axios from 'axios';
import BillReceipt from './BillReceipt';

const API_URL = "https://holisticphysiotherapy.in/holistic_bill/api.php"; 

export default function DuplicateBill() {
  const [searchTerm, setSearchTerm] = useState('');
  const [billsList, setBillsList] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Fetch full bill + line items
  const handleViewBill = async (billNo) => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get(`${API_URL}?action=get_bill_details&bill_no=${encodeURIComponent(billNo)}`);
      if (res.data.status === 'success' && res.data.bill) {
        setSelectedBill(res.data);
      } else {
        setErrorMsg(res.data.message || `Could not load details for ${billNo}`);
      }
    } catch (err) {
      setErrorMsg("Network error while fetching bill receipt.");
    } finally {
      setLoading(false);
    }
  };

  // Main Search
  const handleSearch = async (e) => {
    e?.preventDefault();
    const term = searchTerm.trim();
    if (!term) return;

    setLoading(true);
    setErrorMsg('');
    setSelectedBill(null);
    setBillsList([]);

    try {
      if (term.toUpperCase().startsWith('BILL-')) {
        // Direct search by Bill Number
        await handleViewBill(term);
      } else {
        // Search by UHID
        const res = await axios.get(`${API_URL}?action=get_bill_details&uhid=${encodeURIComponent(term)}`);
        
        if (res.data.status === 'success' && res.data.bills && res.data.bills.length > 0) {
          if (res.data.bills.length === 1) {
            // Only 1 bill found -> Open receipt directly
            await handleViewBill(res.data.bills[0].bill_no);
          } else {
            // Multiple bills found -> Display table list
            setBillsList(res.data.bills);
          }
        } else {
          setErrorMsg(res.data.message || 'No bills found for this UHID.');
        }
      }
    } catch (err) {
      setErrorMsg('Network error while searching.');
    } finally {
      setLoading(false);
    }
  };

  // Render Printable Receipt Screen
  if (selectedBill) {
    return (
      <BillReceipt 
        bill={selectedBill.bill} 
        items={selectedBill.items || []} 
        isDuplicate={true} 
        onBack={() => setSelectedBill(null)}
      />
    );
  }

  return (
    <div className="container mt-4">
      <div className="card shadow-sm border-0 mb-4 d-print-none">
        <div className="card-header text-white" style={{ backgroundColor: "#007C9D" }}>
          <h6 className="mb-0">Duplicate Bill</h6>
        </div>
        <div className="card-body">
          <form onSubmit={handleSearch} className="row g-3 align-items-center">
            <div className="col-md-6">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter UHID or Bill Number (e.g. BILL-1785346840)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button 
                  type="submit" 
                  className="btn text-white fw-bold" 
                  style={{ backgroundColor: "#007C9D" }}
                  disabled={loading}
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>
          </form>

          {errorMsg && (
            <div className="alert alert-danger mt-3 mb-0" role="alert">
              {errorMsg}
            </div>
          )}
        </div>
      </div>

      {/* Multiple Bills Table View */}
      {billsList.length > 0 && (
        <div className="card shadow-sm border-0 d-print-none">
          <div className="card-header bg-light d-flex justify-content-between align-items-center">
            <h6 className="mb-0 fw-bold">
              Found {billsList.length} Bill(s) for UHID: <span className="text-primary">{billsList[0].uhid}</span>
            </h6>
            <span className="badge bg-secondary fs-6">Patient: {billsList[0].patient_name}</span>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Bill No</th>
                    <th>Date & Time</th>
                    <th>Total Amount</th>
                    <th>Payment Mode</th>
                    <th>Billed By</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {billsList.map((bill, index) => (
                    <tr key={bill.id || index}>
                      <td>{index + 1}</td>
                      <td className="fw-bold text-primary">{bill.bill_no}</td>
                      <td>{new Date(bill.bill_date).toLocaleString()}</td>
                      <td className="fw-bold">₹{parseFloat(bill.total_amount || 0).toFixed(2)}</td>
                      <td>
                        <span className="badge bg-info text-dark">{bill.payment_mode}</span>
                      </td>
                      <td>{bill.created_by || 'Admin'}</td>
                      <td className="text-center">
                        <button 
                          className="btn btn-sm btn-outline-primary fw-bold"
                          onClick={() => handleViewBill(bill.bill_no)}
                          disabled={loading}
                        >
                          View/Print
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}